import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener,
  computed, inject, signal, viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule, LucideIconData,
  Search, Loader, Users, HardHat, FileText, FileSpreadsheet,
  Wrench, ShoppingCart, Receipt, FileCheck, CornerDownLeft,
} from 'lucide-angular';
import {
  GlobalSearchService, GlobalSearchGrupo, GlobalSearchItem,
} from '../../../core/services/global-search.service';

/**
 * Buscador global (⌘K / Ctrl+K). Overlay tipo command-palette: input con
 * búsqueda incremental, resultados agrupados por tipo, navegación por teclado
 * (↑↓ Enter Esc) y navegación al detalle al seleccionar. Token-first, sin hex.
 * Se monta una vez (en el topbar) y escucha el atajo globalmente. #402
 */
@Component({
  selector: 'sr-command-palette',
  standalone: true,
  imports: [LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (open()) {
      <div class="cmdk-backdrop" (click)="close()">
        <div class="cmdk-panel" role="dialog" aria-modal="true" aria-label="Búsqueda global"
             (click)="$event.stopPropagation()">
          <div class="cmdk-search">
            <lucide-angular [img]="icons.Search" [size]="18" class="cmdk-search__ic"></lucide-angular>
            <input #searchInput type="text" class="cmdk-search__input"
                   placeholder="Buscar clientes, obras, contratos, equipos, documentos…"
                   (input)="onInput($event)" autocomplete="off" spellcheck="false" aria-label="Texto a buscar">
            @if (loading()) {
              <lucide-angular [img]="icons.Loader" [size]="16" class="cmdk-search__spin"></lucide-angular>
            }
            <button type="button" class="cmdk-esc" (click)="close()" aria-label="Cerrar">esc</button>
          </div>

          <div class="cmdk-results">
            @if (!flatItems().length) {
              <div class="cmdk-hint">
                @if (currentQuery.length >= 2 && !loading()) {
                  Sin resultados para "<strong>{{ currentQuery }}</strong>"
                } @else {
                  Escribe al menos 2 caracteres para buscar en todo el sistema.
                }
              </div>
            } @else {
              @for (g of grupos(); track g.tipo) {
                <div class="cmdk-group">
                  <div class="cmdk-group__label">{{ g.tipoLabel }}</div>
                  @for (it of g.items; track it.ruta) {
                    <button type="button" class="cmdk-item"
                            [class.is-active]="flatIndexOf(it) === selectedIndex()"
                            (click)="go(it)"
                            (mouseenter)="selectedIndex.set(flatIndexOf(it))">
                      <lucide-angular [img]="tipoIcon(g.tipo)" [size]="16" class="cmdk-item__ic"></lucide-angular>
                      <span class="cmdk-item__label">{{ it.label }}</span>
                      @if (it.subtitle) { <span class="cmdk-item__sub">{{ it.subtitle }}</span> }
                    </button>
                  }
                </div>
              }
            }
          </div>

          <div class="cmdk-foot">
            <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
            <span><kbd class="cmdk-foot__ret">↵</kbd> abrir</span>
            <span><kbd>esc</kbd> cerrar</span>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }

    .cmdk-backdrop {
      position: fixed; inset: 0; z-index: 2000;
      background: color-mix(in srgb, var(--ink, #1a1a1a) 45%, transparent);
      backdrop-filter: blur(2px);
      display: flex; align-items: flex-start; justify-content: center;
      padding: 12vh 16px 16px;
    }
    .cmdk-panel {
      width: 100%; max-width: 620px; max-height: 62vh;
      display: flex; flex-direction: column;
      background: var(--surface-card, #fff);
      border: 1px solid var(--hairline, #e7e2dc);
      border-radius: var(--radius-lg, 14px);
      box-shadow: 0 24px 60px -12px color-mix(in srgb, var(--ink, #1a1a1a) 35%, transparent);
      overflow: hidden;
      animation: cmdk-in .12s ease-out;
    }
    @keyframes cmdk-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }

    .cmdk-search {
      display: flex; align-items: center; gap: 10px;
      padding: 14px 16px;
      border-bottom: 1px solid var(--hairline, #e7e2dc);
    }
    .cmdk-search__ic { color: var(--ink-mute, #8a8178); flex: 0 0 auto; }
    .cmdk-search__input {
      flex: 1 1 auto; border: 0; outline: 0; background: transparent;
      font-size: 15px; color: var(--ink, #1a1a1a); font-family: inherit;
    }
    .cmdk-search__input::placeholder { color: var(--ink-faint, #b3aaa0); }
    .cmdk-search__spin { color: var(--accent, #E55408); animation: cmdk-spin 1s linear infinite; }
    @keyframes cmdk-spin { to { transform: rotate(360deg); } }
    .cmdk-esc {
      flex: 0 0 auto; border: 1px solid var(--hairline, #e7e2dc);
      background: var(--surface-ground, #faf8f5); color: var(--ink-mute, #8a8178);
      border-radius: var(--radius-sm, 6px); padding: 2px 7px; font-size: 11px;
      cursor: pointer; font-family: inherit;
    }
    .cmdk-esc:hover { color: var(--ink, #1a1a1a); }

    .cmdk-results { overflow-y: auto; padding: 6px; }
    .cmdk-hint { padding: 26px 16px; text-align: center; color: var(--ink-mute, #8a8178); font-size: 13px; }

    .cmdk-group { padding: 4px 0; }
    .cmdk-group__label {
      padding: 6px 10px 4px; font-size: 11px; font-weight: 600; letter-spacing: .04em;
      text-transform: uppercase; color: var(--ink-faint, #b3aaa0);
    }
    .cmdk-item {
      width: 100%; display: flex; align-items: center; gap: 10px;
      padding: 8px 10px; border: 0; background: transparent; cursor: pointer;
      border-radius: var(--radius-md, 9px); text-align: left; font-family: inherit;
      color: var(--ink, #1a1a1a);
    }
    .cmdk-item.is-active { background: var(--accent-tint, #fdeee6); }
    .cmdk-item.is-active .cmdk-item__ic { color: var(--accent, #E55408); }
    .cmdk-item__ic { color: var(--ink-mute, #8a8178); flex: 0 0 auto; }
    .cmdk-item__label { font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 0 1 auto; }
    .cmdk-item__sub {
      font-size: 12px; color: var(--ink-mute, #8a8178); margin-left: auto;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 45%; padding-left: 10px;
    }

    .cmdk-foot {
      display: flex; gap: 16px; padding: 9px 16px;
      border-top: 1px solid var(--hairline, #e7e2dc);
      background: var(--surface-ground, #faf8f5);
      font-size: 11px; color: var(--ink-mute, #8a8178);
    }
    .cmdk-foot kbd {
      display: inline-block; min-width: 16px; text-align: center;
      border: 1px solid var(--hairline, #e7e2dc); border-radius: 4px;
      padding: 0 4px; margin-right: 3px; background: var(--surface-card, #fff);
      font-family: inherit; font-size: 11px;
    }

    @media (max-width: 640px) {
      .cmdk-backdrop { padding: 8vh 10px 10px; }
      .cmdk-item__sub { display: none; }
    }
  `],
})
export class SrCommandPaletteComponent {
  private router = inject(Router);
  private searchSvc = inject(GlobalSearchService);
  private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  readonly open = signal(false);
  readonly loading = signal(false);
  readonly grupos = signal<GlobalSearchGrupo[]>([]);
  readonly selectedIndex = signal(0);
  readonly flatItems = computed(() => this.grupos().flatMap(g => g.items));
  currentQuery = '';

  private readonly query$ = new Subject<string>();

  readonly icons = { Search, Loader, CornerDownLeft };
  private readonly tipoIcons: Record<string, LucideIconData> = {
    CLIENTE: Users, OBRA: HardHat, CONTRATO: FileText, COTIZACION: FileSpreadsheet,
    EQUIPO: Wrench, ORDEN_COMPRA: ShoppingCart, ESTADO_PAGO: Receipt, DOCUMENTO_TRIBUTARIO: FileCheck,
  };

  constructor() {
    this.query$.pipe(
      debounceTime(180),
      distinctUntilChanged(),
      tap(q => {
        this.currentQuery = q.trim();
        this.loading.set(this.currentQuery.length >= 2);
        if (this.currentQuery.length < 2) { this.grupos.set([]); }
      }),
      switchMap(q => q.trim().length < 2 ? of({ query: q, total: 0, grupos: [] }) : this.searchSvc.search(q, 6)),
      takeUntilDestroyed(),
    ).subscribe(res => {
      this.grupos.set(res.grupos ?? []);
      this.selectedIndex.set(0);
      this.loading.set(false);
    });

    // Apertura desde fuera del topbar (ej. botón "Buscar" del dashboard).
    this.searchSvc.openRequests$.pipe(takeUntilDestroyed()).subscribe(() => this.openPalette());
  }

  /** API pública: el topbar la invoca desde el botón de búsqueda. */
  openPalette(): void {
    this.open.set(true);
    this.grupos.set([]);
    this.currentQuery = '';
    this.selectedIndex.set(0);
    setTimeout(() => {
      const el = this.searchInput()?.nativeElement;
      if (el) { el.value = ''; el.focus(); }
    });
  }

  close(): void { this.open.set(false); }

  onInput(e: Event): void {
    this.query$.next((e.target as HTMLInputElement).value);
  }

  flatIndexOf(it: GlobalSearchItem): number { return this.flatItems().indexOf(it); }

  tipoIcon(tipo: string): LucideIconData { return this.tipoIcons[tipo] ?? FileText; }

  go(it: GlobalSearchItem): void {
    this.close();
    this.router.navigateByUrl(it.ruta);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      this.open() ? this.close() : this.openPalette();
      return;
    }
    if (!this.open()) { return; }
    switch (e.key) {
      case 'Escape': e.preventDefault(); this.close(); break;
      case 'ArrowDown': e.preventDefault(); this.move(1); break;
      case 'ArrowUp': e.preventDefault(); this.move(-1); break;
      case 'Enter': {
        e.preventDefault();
        const it = this.flatItems()[this.selectedIndex()];
        if (it) { this.go(it); }
        break;
      }
    }
  }

  private move(delta: number): void {
    const n = this.flatItems().length;
    if (!n) { return; }
    this.selectedIndex.set((this.selectedIndex() + delta + n) % n);
    setTimeout(() => document.querySelector('.cmdk-item.is-active')?.scrollIntoView({ block: 'nearest' }));
  }
}
